'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getChats, 
  getChat, 
  saveChat as saveChatToStorage, 
  deleteChat as deleteChatFromStorage, 
  generateChatTitle, 
  updateChatTitle as updateChatTitleInStorage, 
  clearAllChats as clearAllChatsFromStorage,
  type StoredChat, 
  chatStorageEvents 
} from '@/lib/chat-storage';
import type { ChatMessage } from '@/lib/types';
import type {PromptType} from "@/app/(chat)/api/chat/route";

export function useChatHistory(userId?: string | null, companyName?: string | null, userName?: string | null) {
  const [chats, setChats] = useState<StoredChat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const loadingRef = useRef(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced load function to prevent excessive API calls
  const debouncedLoadChats = useCallback(async () => {
    if (loadingRef.current) return; // Prevent concurrent loads
    
    loadingRef.current = true;
    try {
      const storedChats = await getChats(userId, companyName, userName);
      setChats(storedChats);
    } catch (error) {
      console.error('Error loading chats from database:', error);
      setChats([]);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [userId, companyName, userName]);

  // Load chats from database on mount and listen for changes
  useEffect(() => {
    // Initial load
    debouncedLoadChats();

    // Subscribe to storage events for real-time updates with debouncing
    const unsubscribe = chatStorageEvents.subscribe(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      debounceTimeoutRef.current = setTimeout(() => {
        debouncedLoadChats();
      }, 300); // 300ms debounce
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [debouncedLoadChats]);

  const getChatById = useCallback(async (id: string): Promise<StoredChat | null> => {
    try {
      return await getChat(id, userId, companyName, userName);
    } catch (error) {
      console.error('Error getting chat by ID:', error);
      return null;
    }
  }, [userId, companyName, userName]);

  const saveChat = useCallback(async (id: string, messages: ChatMessage[], promptMode?: PromptType, title?: string, forceUpdate = false) => {
    try {
      const existingChat = await getChatById(id);
      
      // Check if messages have actually changed
      const messagesChanged = !existingChat || 
        existingChat.messages.length !== messages.length ||
        JSON.stringify(existingChat.messages) !== JSON.stringify(messages);
      
      if (!messagesChanged && !forceUpdate && !title) {
        return;
      }
      
      const now = new Date().toISOString();
      const chatTitle = generateChatTitle();

      const chat: StoredChat = {
        id,
        title: chatTitle,
        messages,
        userId: userId || null, // Set userId for the conversation
        companyName: companyName || null, // Set companyName for the conversation
        userName: userName || null, // Set userName for the conversation
        promptMode: promptMode || existingChat?.promptMode || 'simple', // Set promptMode for the conversation
        price: existingChat?.price || 0, // Preserve existing price or initialize with 0
        createdAt: existingChat?.createdAt || now,
        // Only update timestamp if messages actually changed or forced
        updatedAt: (messagesChanged || forceUpdate) ? now : (existingChat?.updatedAt || now),
      };

      // Skip event emission for regular message saves to prevent infinite loops
      await saveChatToStorage(chat, true);
      // Note: We skip event emission to prevent infinite API call loops
    } catch (error) {
      console.error('Error saving chat:', error);
    }
  }, [getChatById, userId, companyName, userName]);

  const deleteChat = useCallback(async (id: string) => {
    try {
      await deleteChatFromStorage(id);
      // Note: Local state will be updated automatically via the event system
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  }, []);

  const updateChatTitle = useCallback(async (id: string, title: string) => {
    try {
      await updateChatTitleInStorage(id, title);
      // Note: Local state will be updated automatically via the event system
    } catch (error) {
      console.error('Error updating chat title:', error);
    }
  }, []);

  const clearAllChats = useCallback(async () => {
    try {
      await clearAllChatsFromStorage();
      // Note: Local state will be updated automatically via the event system
    } catch (error) {
      console.error('Error clearing all chats:', error);
    }
  }, []);

  return {
    chats,
    isLoading,
    saveChat,
    deleteChat,
    getChat: getChatById,
    updateChatTitle,
    clearAllChats,
  };
}
